import { Link } from "react-router-dom";
import { useState } from "react";

export default function BonPlansMobile({ promoProducts }) {
  const formatPriceDT = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) return "N/A";
    const isWhole = num % 1 === 0;
    return `${new Intl.NumberFormat("fr-FR", {
      minimumFractionDigits: isWhole ? 0 : 3,
      maximumFractionDigits: 3,
    }).format(num)} DT`;
  };

  const getPromoLabel = (product) => {
    if (product.promo_type === "exact" || !product.promo_percentage) {
      if (product.displayPrice && product.discountedPrice && product.displayPrice > 0) {
        const pct = Math.round(
          ((product.displayPrice - product.discountedPrice) / product.displayPrice) * 100
        );
        return pct > 0 ? `-${pct}%` : "Promo";
      }
      return "Promo";
    }
    return `-${product.promo_percentage}%`;
  };

  return (
    <>
      <style>{`
        .bonplans-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          padding: 0 4px;
          width: 100%;
          box-sizing: border-box;
        }
        @media (max-width: 360px) {
          .bonplans-grid { grid-template-columns: 1fr; }
        }
        .bonplans-card {
          background: #fff;
          border-radius: 16px;
          box-shadow: 0 2px 12px rgba(0,0,0,0.08);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-decoration: none;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          width: 100%;
          min-height: 260px;
        }
        .bonplans-card:active {
          transform: scale(0.97);
        }
        .bonplans-img-wrap {
          width: 100%;
          height: 140px;
          position: relative;
          box-sizing: border-box;
          border-bottom: 1px solid #f0f0f0;
          background-color: #fafafa;
        }
        /* background-image fills the container, no <img> tag needed */
        .bonplans-bg-img {
          width: 100%;
          height: 100%;
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center;
        }
        /* Fallback emoji shown when no image */
        .bonplans-no-img {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2.5rem;
          color: #ddd;
        }
        .bonplans-promo-badge {
          position: absolute;
          top: 8px;
          right: 8px;
          background: #81d9ff;
          color: #fff;
          font-size: 0.68rem;
          font-weight: 700;
          padding: 3px 7px;
          border-radius: 20px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.15);
          white-space: nowrap;
          z-index: 2;
        }
        .bonplans-body {
          width: 100%;
          padding: 8px 8px 12px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          flex: 1;
        }
        .bonplans-name {
          font-size: 0.78rem;
          font-weight: 700;
          color: #222;
          line-height: 1.3;
          margin: 0;
          word-break: break-word;
        }
        .bonplans-variation {
          font-size: 0.7rem;
          color: #888;
          margin: 0;
        }
        .bonplans-cats {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 3px;
        }
        .bonplans-cat-chip {
          background: #f1f8e9;
          color: #558b2f;
          font-size: 0.6rem;
          font-weight: 600;
          padding: 2px 6px;
          border-radius: 10px;
        }
        .bonplans-old-price {
          text-decoration: line-through;
          color: #aaa;
          font-size: 0.75rem;
        }
        .bonplans-new-price {
          color: #ff1fb2;
          font-size: 1rem;
          font-weight: 800;
        }
        .bonplans-en-promo {
          background: #ff1fb2;
          color: #fff;
          font-size: 0.6rem;
          font-weight: 700;
          padding: 3px 8px;
          border-radius: 6px;
          margin-top: 2px;
        }
      `}</style>

      <div className="bonplans-grid">
        {promoProducts.map((product) => {
          let categories = [];
          try {
            if (typeof product.category === "string") {
              categories = JSON.parse(product.category);
            } else if (Array.isArray(product.category)) {
              categories = product.category;
            } else if (product.category) {
              categories = [product.category];
            }
            if (!Array.isArray(categories)) categories = [categories];
          } catch {
            categories = product.category ? [product.category] : [];
          }

          return (
            <Link
              key={`${product.type}-${product.id}-${product.variation?.id || ""}`}
              to={`/shop-details/${product.id}`}
              className="bonplans-card"
            >
              {/* IMAGE via background-image — avoids .jfif/CORS img tag issues */}
              <div className="bonplans-img-wrap">
                {product.image ? (
                  <div
                    className="bonplans-bg-img"
                    style={{ backgroundImage: `url("${product.image}")` }}
                  />
                ) : (
                  <div className="bonplans-no-img">🐾</div>
                )}
                <span className="bonplans-promo-badge">
                  {getPromoLabel(product)}
                </span>
              </div>

              {/* BODY */}
              <div className="bonplans-body">
                <p className="bonplans-name">{product.name}</p>

                {product.type === "variation" && product.variation_name && (
                  <p className="bonplans-variation">{product.variation_name}</p>
                )}

                {categories.length > 0 && (
                  <div className="bonplans-cats">
                    {categories.slice(0, 2).map((cat, i) => (
                      <span key={i} className="bonplans-cat-chip">{cat}</span>
                    ))}
                  </div>
                )}

                <span className="bonplans-old-price">
                  {formatPriceDT(product.displayPrice)}
                </span>
                <span className="bonplans-new-price">
                  {formatPriceDT(product.discountedPrice)}
                </span>

                <span className="bonplans-en-promo">En Promotion</span>
              </div>
            </Link>
          );
        })}
      </div>
    </>
  );
}